// @ts-nocheck
/**
 * Pure run-merge that turns a prepared track-point array into GeoJSON LineString features for the
 * native Mapbox GL track layer (see components/gl/GlMapTrackLayer.tsx).
 *
 * This is the GL analogue of SegmentedTrackRenderer's SVG path building: it applies the SAME
 * gap / leg-boundary-bridge / cut-range edge rules, then merges consecutive edges that share the
 * same (color, width) into a single LineString — one Feature instead of one SVG <line> per edge.
 *
 * Kept side-effect free (all state passed in) so it is unit-testable and shared by explore + fleet.
 * `getColor` already returns the greyed base color when event selections exist; the colored event
 * overlays are emitted separately into `selection`.
 */

import { simplifyTrackRun, type TrackImportance } from "../utils/trackGeometry";

export interface BuildTrackFeaturesParams {
  /** Validated, time-window-filtered, LOD-sampled points (same array the SVG renderer receives). */
  data: any[];
  /** From useTrackRendering — returns per-point color for the active maptype. */
  getColor: (d: any, prev: any | null, cfg: any) => string;
  /** From useTrackRendering — returns per-point stroke width for the active maptype. */
  getThickness: (d: any, prev: any | null, cfg: any) => number | string;
  /** effectiveConfig (maptype, zoomLevel, showGaps, sourceColor, …). */
  config: any;
  getLat: (d: any) => number | undefined;
  getLng: (d: any) => number | undefined;
  getTimestamp: (d: any) => Date;
  /** LOD-adjusted sampling frequency, used for gap-threshold sizing (mirrors the SVG renderer). */
  samplingFrequency: number;
  /** Cut-mode state (store.isCut() / store.cutEvents()). */
  cut?: { isCut: boolean; cutEvents: any[] };
  /** Event-selection ranges (selectionStore.selectedRanges()). */
  selectedRanges?: Array<{ start_time: string | number | Date; end_time: string | number | Date }>;
  /**
   * (range, index, total) -> stroke. Callers bind utils/colorScale `resolveSelectionRangeStrokeColor`,
   * closing over `allSelectedEventIds()` so the color comes from the event's selection index rather
   * than `index`. The union, not `selectedEvents()`: the map draws both selection domains.
   */
  resolveRangeColor?: (range: any, index: number, total: number) => string;
  /** Width used for the colored event-selection overlay (SVG uses 3). */
  selectionWidth?: number;
  /** Max gap (ms) bridged at a consecutive leg boundary. Single track = 2500; fleet = 10000. */
  legBridgeMs?: number;
  /** Extra properties merged onto every base feature (e.g. { sourceId, opacity } for fleet). */
  baseFeatureProps?: Record<string, unknown>;
  /**
   * Optional RDP filter applied AFTER color/gap runs are built. Endpoints of each run are kept,
   * so a color change stays a feature boundary and the feature's color is unchanged.
   */
  simplify?: { importance: TrackImportance; tolerance: number };
}

export interface TrackFeatureCollections {
  base: { type: 'FeatureCollection'; features: any[] };
  selection: { type: 'FeatureCollection'; features: any[] };
}

const emptyFc = () => ({ type: 'FeatureCollection' as const, features: [] as any[] });

const lineString = (
  coords: number[][],
  props: Record<string, unknown>
) => ({
  type: 'Feature' as const,
  geometry: { type: 'LineString' as const, coordinates: coords },
  properties: props,
});

export function buildTrackFeatures(params: BuildTrackFeaturesParams): TrackFeatureCollections {
  const {
    data,
    getColor,
    getThickness,
    config,
    getLat,
    getLng,
    getTimestamp,
    samplingFrequency,
    cut,
    selectedRanges,
    resolveRangeColor,
    selectionWidth = 3,
    legBridgeMs = 2500,
    baseFeatureProps,
    simplify,
  } = params;

  const cfg: any = config || {};
  const result: TrackFeatureCollections = { base: emptyFc(), selection: emptyFc() };

  if (!data || data.length < 2) return result;

  const hasCutRanges = !!(cut && cut.isCut && cut.cutEvents && cut.cutEvents.length > 0);
  const getCutRangeIndex = (point: any): number => {
    if (!hasCutRanges) return -1;
    const pointTime = getTimestamp(point).getTime();
    for (let i = 0; i < cut!.cutEvents.length; i++) {
      const range = cut!.cutEvents[i];
      if (typeof range === 'number') continue;
      if (range.start_time && range.end_time) {
        const startTime = new Date(range.start_time).getTime();
        const endTime = new Date(range.end_time).getTime();
        if (pointTime >= startTime && pointTime <= endTime) return i;
      }
    }
    return -1;
  };

  // ── 1. Build drawable edges, skipping gaps / cut boundaries (port of SegmentedTrackRenderer) ──
  type PreparedSeg = { p0: any; p1: any; color: string; width: number | string };
  const prepared: PreparedSeg[] = [];

  const zoomLevel = cfg.zoomLevel ?? 16;
  const disableGapDetection = zoomLevel < 16;
  const expectedInterval = 1000 / (samplingFrequency || 1);
  const gapThreshold = cfg.gapThreshold ? cfg.gapThreshold : Math.min(expectedInterval * 3, 1000);
  const legBoundaryBridgeMs = legBridgeMs;

  for (let i = 0; i < data.length - 1; i++) {
    const curr = data[i];
    const next = data[i + 1];

    const currLat = getLat(curr);
    const currLng = getLng(curr);
    const nextLat = getLat(next);
    const nextLng = getLng(next);
    if (
      currLng === undefined || currLat === undefined || nextLng === undefined || nextLat === undefined ||
      isNaN(currLng) || isNaN(currLat) || isNaN(nextLng) || isNaN(nextLat)
    ) {
      continue;
    }

    let isCutRangeBoundary = false;
    if (hasCutRanges) {
      if (getCutRangeIndex(curr) !== getCutRangeIndex(next)) isCutRangeBoundary = true;
    }

    const timeDiff = Math.abs(getTimestamp(curr).getTime() - getTimestamp(next).getTime());
    const prevLeg = curr.leg_number ?? curr.Leg_number ?? curr.LEG;
    const nextLeg = next.leg_number ?? next.Leg_number ?? next.LEG;
    const prevLegNum = prevLeg != null && !isNaN(Number(prevLeg)) ? Number(prevLeg) : undefined;
    const nextLegNum = nextLeg != null && !isNaN(Number(nextLeg)) ? Number(nextLeg) : undefined;
    const isConsecutiveLegBoundary =
      prevLegNum !== undefined && nextLegNum !== undefined &&
      timeDiff > gapThreshold && timeDiff <= legBoundaryBridgeMs &&
      Math.abs(nextLegNum - prevLegNum) === 1;
    const bridgeLegBoundary = isConsecutiveLegBoundary && !isCutRangeBoundary;
    // The appended playhead endpoint (boat head) must always connect, regardless of gap/LOD spacing.
    const bridgeEndpoint = next && next.__glEndpoint === true;

    if (!disableGapDetection) {
      if ((timeDiff > gapThreshold || isCutRangeBoundary) && !bridgeLegBoundary && !bridgeEndpoint) continue;
    } else if (isCutRangeBoundary && !bridgeEndpoint) {
      continue;
    }

    prepared.push({
      p0: curr,
      p1: next,
      color: getColor(curr, next, cfg),
      width: Number(getThickness(curr, next, cfg)) || 1,
    });
  }

  // ── 2. Merge consecutive edges with identical (color, width) into runs → LineString features ──
  type MergedRun = { points: any[]; color: string; width: number | string };
  const runs: MergedRun[] = [];
  const styleKey = (c: string, w: number | string) => `${c}\0${String(w)}`;

  for (const seg of prepared) {
    const last = runs[runs.length - 1];
    if (
      last &&
      styleKey(last.color, last.width) === styleKey(seg.color, seg.width) &&
      last.points[last.points.length - 1] === seg.p0
    ) {
      last.points.push(seg.p1);
    } else {
      runs.push({ points: [seg.p0, seg.p1], color: seg.color, width: seg.width });
    }
  }

  for (const run of runs) {
    if (run.points.length < 2) continue;
    // Fewer vertices on straight legs. The run's color and its endpoints (color boundaries) stay.
    const drawn = simplify
      ? simplifyTrackRun(run.points, simplify.importance, simplify.tolerance)
      : run.points;
    const coords: number[][] = [];
    for (const p of drawn) {
      const lng = getLng(p);
      const lat = getLat(p);
      if (lng === undefined || lat === undefined || isNaN(lng) || isNaN(lat)) continue;
      coords.push([lng, lat]);
    }
    if (coords.length < 2) continue;
    result.base.features.push(
      lineString(coords, {
        color: run.color,
        width: Number(run.width) || 1,
        t0: getTimestamp(drawn[0]).getTime(),
        t1: getTimestamp(drawn[drawn.length - 1]).getTime(),
        ...(baseFeatureProps || {}),
      })
    );
  }

  // ── 3. Event-selection overlays: one colored feature per range (port of the SVG overlay pass) ──
  if (selectedRanges && selectedRanges.length > 0 && resolveRangeColor) {
    selectedRanges.forEach((range, rangeIndex) => {
      const startTime = new Date(range.start_time).getTime();
      const endTime = new Date(range.end_time).getTime();
      const rangePoints: any[] = [];
      for (const point of data) {
        const t = getTimestamp(point).getTime();
        if (t < startTime || t > endTime) continue;
        rangePoints.push(point);
      }
      const drawnRange = simplify
        ? simplifyTrackRun(rangePoints, simplify.importance, simplify.tolerance)
        : rangePoints;
      const coords: number[][] = [];
      for (const point of drawnRange) {
        const lng = getLng(point);
        const lat = getLat(point);
        if (lng === undefined || lat === undefined || isNaN(lng) || isNaN(lat)) continue;
        coords.push([lng, lat]);
      }
      if (coords.length < 2) return;
      result.selection.features.push(
        lineString(coords, {
          color: resolveRangeColor(range, rangeIndex, selectedRanges.length),
          width: selectionWidth,
          rangeIndex,
        })
      );
    });
  }

  return result;
}
